import React, { useContext } from "react";
import Button from "Components/button/Button";
import DomainStatusBadge from "Components/domainStatusBadge/DomainStatusBadge";
import ResponsiveTable from "Components/responsiveTable/ResponsiveTable";
import FullPaginationControls from "Components/fullPaginationControls/FullPaginationControls";
import TranslatedString from "Components/TranslatedString";
import withConfirmationModal from "Components/withConfirmationModal";
import { t } from "i18next";
import ArchiveEmailAccountRow from "Components/archiveEmailAccountRow/ArchiveEmailAccountRow";
import { Icon, plus, mail, addArchive } from "Icons/index";
import qs from "query-string";
import cx from "classnames";
import s from "./listEmailAccounts.scss";
import { MediaQueriesContext, AuthContext } from "@/App";
import { useApiQuery, useApiMutation } from "@/hooks";
import { useHistory, useLocation } from "react-router-dom";

const STATUSES = ["activating", "disabling", "enabling", "deleting", "updating"];

const ArchiveEmailAccountsListScreen = ({ openConfirmationModal, openDisablingConfirmationModal }) => {
  const history = useHistory();
  const location = useLocation();
  const { isMobileDevice, isMediaMobile } = useContext(MediaQueriesContext);
  const { getLoggedUser } = useContext(AuthContext);

  const domainCode = location.pathname.split("/")[3];

  const {
    data: emailAccounts = [],
    pagination: ArchiveEmailAccountsPaginationInfo,
    isLoading: ArchiveEmailAccountsIsLoading,
    isRefreshing: ArchiveEmailAccountsIsRefreshing,
    getData: getArchiveEmailAccounts,
    updateData: updateArchiveEmailAccounts,
  } = useApiQuery("getArchiveEmailAccounts", {
    enablePagination: true,
    requestParams: domainCode,
    autoRefresh: {
      shouldRefresh: (data) => data.some(({ archive_status }) => STATUSES.includes(archive_status)),
      interval: 4000,
    },
  });

  const { mutate: deleteEmail } = useApiMutation("deleteArchiveEmail", {
    optimistic: (emailCode) =>
      updateArchiveEmailAccounts((prev) =>
        prev.map((email) => (email.code === emailCode ? { ...email, archive_status: "deleting" } : email))
      ),
  });

  const { mutate: updateEmailStatus } = useApiMutation("editArchiveEmailAccountStatus", {
    optimistic: (emailCode, _, newStatus) =>
      updateArchiveEmailAccounts((prev) =>
        prev.map((email) => (email.code === emailCode ? { ...email, archive_status: newStatus } : email))
      ),
  });

  const handleAddNew = () => {
    history.push({
      pathname: `/archive/domains/${domainCode}/email_accounts/new`,
      state: {
        prevPagination: location.search,
      },
    });
  };

  const handleBackToList = () => {
    history.push("/archive");
  };

  const handleDeleteEmail = async (emailCode) => {
    try {
      await deleteEmail(emailCode, domainCode);
    } catch (error) {
      console.error("Error deleting archive email:", error);
    }
  };

  const handleStatusChange = async (emailCode, newStatus) => {
    try {
      await updateEmailStatus(emailCode, domainCode, newStatus);
    } catch (error) {
      console.error("Error changing archive email status:", error);
    }
  };

  const loggedUser = getLoggedUser();
  const canAddArchive = !(
    loggedUser.type === "TeamMember" && !loggedUser.permissions.includes("create_vault_email_accounts")
  );
  const query = qs.parse(location.search);

  const tableHeaders = [
    {
      name: t("table.header.status"),
      size: isMobileDevice ? "2rem" : "4rem",
      center: true,
    },
    {
      name: t("table.header.account"),
      key: "name",
    },
    {
      name: t("table.header.retention"),
      size: "6rem",
      hideOnMobile: true,
    },
    {
      name: t("table.header.delegates"),
      size: isMobileDevice ? "3rem" : "5rem",
    },
    {
      size: "3rem",
      center: true,
    },
  ];

  const filterLegends = {
    all: <Icon svg={mail} width="0.75rem" height="0.75rem" />,
    enabled: <DomainStatusBadge status="enabled" isHealthy showAsPreview />,
    disabled: <DomainStatusBadge status="disabled" isHealthy showAsPreview />,
  };

  if (emailAccounts.length === 0 && !query.archive_status && !query.query) {
    return (
      <div className={cx(s.listEmailAccounts)}>
        <div className={s.illustrationMsg}>
          <Icon svg={addArchive} width="10rem" height="10rem" />
          <div>{t("infoMsg.not.achive.domain")}</div>
          <a className="inlineLink" onClick={handleBackToList}>
            {t("button.back.domains")}
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className={cx(s.listEmailAccounts)}>
      <div className={s.wrapInfoBtn}>
        {!isMediaMobile && <div className={s.infoText} />}
        {canAddArchive && (
          <Button className={s.addBtn} icon={plus} onClick={handleAddNew}>
            button.addEmail
          </Button>
        )}
      </div>

      <FullPaginationControls
        className={s.filters}
        paginationInfo={ArchiveEmailAccountsPaginationInfo}
        location={location}
        refreshList={() => getArchiveEmailAccounts(domainCode)}
        listFilterParam="archive_status"
        listFilters={["all", "enabled", "disabled"]}
        listFilterLegends={filterLegends}
        isLoading={ArchiveEmailAccountsIsLoading}
        totals={ArchiveEmailAccountsPaginationInfo?.totals}
      />

      <div className={s.table}>
        <ResponsiveTable
          history={history}
          areRowsLoading={ArchiveEmailAccountsIsLoading && !ArchiveEmailAccountsIsRefreshing}
          location={location}
          headers={tableHeaders}
          rows={emailAccounts.map((emailAccount) => (
            <ArchiveEmailAccountRow
              key={emailAccount.code}
              emailAccount={emailAccount}
              location={location}
              deleteEmail={() =>
                openConfirmationModal(
                  emailAccount,
                  handleDeleteEmail,
                  <TranslatedString subs={{ emailAccount: emailAccount.email_address }}>
                    modal.delete.archive.emailAccount.text
                  </TranslatedString>
                )
              }
              toggleStatus={(newStatus) => {
                if (emailAccount.archive_status === "disabled") {
                  handleStatusChange(emailAccount.code, newStatus);
                } else {
                  openDisablingConfirmationModal(
                    emailAccount,
                    (code) => handleStatusChange(code, newStatus),
                    <TranslatedString subs={{ emailAccount: emailAccount.email_address }}>
                      modal.disable.archive.emailAccount.text
                    </TranslatedString>
                  );
                }
              }}
            />
          ))}
        />
      </div>
    </div>
  );
};

export default withConfirmationModal(
  withConfirmationModal(ArchiveEmailAccountsListScreen, {
    title: <TranslatedString>modal.delete.archive.emailAccount.title</TranslatedString>,
    buttonLabel: "button.delete",
  }),
  {
    title: <TranslatedString>modal.disable.archive.emailAccount.title</TranslatedString>,
    name: "DisablingConfirmation",
    buttonLabel: "button.disable",
  }
);
